import React, { useEffect, useState, useRef, useMemo, useLayoutEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
    AnalyticsViewMode,
    Dict,
    PromptType,
    QueryType,
    RootState,
    UserRole,
    AnalyticsFilter,
} from 'app/types'
import { useAdminPermissions, useAppDispatch, useAppSelector } from 'app/hooks'
import { icons, analyticsIcons } from 'app/constants'
import {
    createAnalyticsDashboard,
    createAnalyticsTile,
    removeAnalyticsTile,
    updateAnalyticsDashboard,
    updateAnalyticsTile,
    removeAnalyticsDashboard,
    setAnalyticsDashboardDefault
} from 'actions/adminActions'
import { showPrompt, setQuery } from 'actions/appActions'
import {
    BackButton,
    Button,
    Card,
    DropdownMenu,
    Icon,
    IconButton,
    Input,
    Spinner,
    DateRange
} from 'components'
import * as fnc from 'helpers/fnc'
import { logger } from 'helpers/logger'
import { AnalyticsTable } from './AnalyticsTable'
import { getTile } from 'services/adminServices'
import {
    buildAnalyticsDashboardsRequest,
    buildAnalyticsTilesRequest,
    fetchAnalyticsDashboardsRequest,
    fetchAnalyticsTilesRequest,
} from 'services/analyticsQueryService'

interface AnalyticsTileItem {
    id: string
    name: string
    appId: string
    organizationId: string
    config: Dict
    createdAt: string
    updatedAt: string
}

// Layout mode options for dashboard display
enum DashboardLayout {
    Single = 'single',   // One tile per page (full width)
    Dual = 'dual',       // Two tiles per page (left/right)
    Grid = 'grid'        // 4x4 grid layout
}

interface AnalyticsDashboardProps {
    app: AppData
    definitions: any
    filters: any
    optionalFilters: any[]
    searchFilters: any[]
    additionalMaps: any
}

function normalizeDashboard(dashboard: any) {
    if (!dashboard) {
        return dashboard
    }

    let layoutConfig = dashboard.layoutConfig
    if (typeof layoutConfig === 'string') {
        try {
            layoutConfig = JSON.parse(layoutConfig)
        } catch (error) {
            layoutConfig = null
        }
    }

    return {
        ...dashboard,
        layoutConfig: layoutConfig && typeof layoutConfig === 'object' ? layoutConfig : {},
    }
}

export function AnalyticsDashboard(props: AnalyticsDashboardProps) {
    const { app, definitions, filters, optionalFilters, searchFilters, additionalMaps } = props

    const dispatch = useAppDispatch()
    const queryClient = useQueryClient()
    const adminPermission = useAdminPermissions()
    const superAdmin = adminPermission(app, null, UserRole.AdminBackup)
    const organization = useAppSelector((state: RootState) => state.admin.organization)

    const [loading, setLoading] = useState(true)
    const [tiles, setTiles] = useState<AnalyticsTileItem[]>([])
    const [dashboards, setDashboards] = useState<any[]>([])
    const [selectedDashboard, setSelectedDashboard] = useState<any>(null)
    const [activeTile, setActiveTile] = useState<AnalyticsTileItem | null>(null)
    const [editMode, setEditMode] = useState(false)
    const topbarSlotRef = useRef<HTMLDivElement>(null)
    const [topbarPortalEl, setTopbarPortalEl] = useState<HTMLElement | null>(null)
    const isViewingRef = useRef(false)
    useLayoutEffect(() => {
        const nowViewing = activeTile != null
        if (nowViewing !== isViewingRef.current) {
            isViewingRef.current = nowViewing
        }
        setTopbarPortalEl(nowViewing ? topbarSlotRef.current : null)
    }, [activeTile])
    const [currentConfig, setCurrentConfig] = useState<Dict | null>(null)
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
    const [tileViewKey, setTileViewKey] = useState(0)

    // Edit header controlled fields
    const [editName, setEditName] = useState('')
    const [editIcon, setEditIcon] = useState('fas fa-chart-bar')
    const [editDescription, setEditDescription] = useState('')

    // Dashboard layout mode state
    const [layoutMode, setLayoutMode] = useState<DashboardLayout>(DashboardLayout.Grid)

    // Pagination state - TILES_PER_PAGE is now based on layout mode
    const getTilesPerPage = () => {
        switch (layoutMode) {
            case DashboardLayout.Single: return 1
            case DashboardLayout.Dual: return 2
            case DashboardLayout.Grid: return 4  // Changed from 16 to 4 (2x2 grid)
            default: return 4
        }
    }

    const TILES_PER_PAGE = getTilesPerPage()
    const [currentPage, setCurrentPage] = useState(1)
    const [totalPages, setTotalPages] = useState(1)

    // Dashboard edit mode state
    const [tileChanges, setTileChanges] = useState<{ [key: string]: { order?: number, size?: number } }>({})

    // Refresh state to force grid recalculation
    const [refresh, setRefresh] = useState<number>(Date.now())

    // Dashboard date filter — single source of truth is Redux queries['analytics']['date']
    const analyticsDateQuery = useAppSelector((state: RootState) => (state.app.queries[QueryType.Analytics] as any)?.[AnalyticsFilter.Date] ?? null)

    const initialLoad = useRef(0)
    const seededDashboardIdRef = useRef<number | null>(null)
    const [delayTileRender, setDelayTileRender] = useState(false)

    // Default date range for analytics (1 year back from today) - used as initial Redux seed
    const defaultDateRange = useMemo(() => {
        const minDate = new Date()
        minDate.setFullYear(minDate.getFullYear() - 1)
        minDate.setHours(0, 0, 0, 0)
        const maxDate = new Date()
        maxDate.setHours(23, 59, 59, 0)
        return {
            startDate: minDate.toMySQLDate(),
            endDate: maxDate.toMySQLDate()
        }
    }, [])

    // Ref to track dashboard creation to prevent duplicates from React's double useEffect calls
    const defaultDashboardCreationInProgress = useRef(false)

    // Calculate margin modifier based on layout mode
    const marginModifier = useMemo(() => {
        switch (layoutMode) {
            case DashboardLayout.Single: return 1.0      // Full size, no reduction
            case DashboardLayout.Dual: return 0.666     // 2x1 layout
            case DashboardLayout.Grid: return 0.333     // 4x4 layout
            default: return 1.0
        }
    }, [layoutMode])

    // Load dashboard tiles on component mount
    useEffect(() => {
        if (!app && !organization) {
            return
        }
        loadDashboards()
    }, [app, organization])

    // Load dashboard tiles when selected dashboard changes
    useEffect(() => {
        if (selectedDashboard) {
            loadDashboardTiles(selectedDashboard.id)

            // Only seed the date filter when switching to a genuinely different dashboard.
            // selectedDashboard gets a new object reference whenever loadDashboardTiles updates
            // the store — we must NOT re-apply the saved date on those reference-only changes
            // or it will overwrite whatever date the user has chosen.
            if (seededDashboardIdRef.current === selectedDashboard.id) {
                return
            }
            seededDashboardIdRef.current = selectedDashboard.id

            const layoutConfig = normalizeDashboard(selectedDashboard).layoutConfig

            // If the analytics date is already active in Redux, keep it.
            // This preserves the user's chosen range when navigating between
            // the dashboard and individual analytics views.
            if (analyticsDateQuery) {
                return
            }

            // Load dashboard date filter from configuration into Redux
            if (layoutConfig && layoutConfig.dateFilter) {
                const dateFilter = layoutConfig.dateFilter
                dispatch(setQuery({
                    query: { [AnalyticsFilter.Date]: { startDate: dateFilter.startDate, endDate: dateFilter.endDate } },
                    type: QueryType.Analytics,
                }))
            } else if (!analyticsDateQuery) {
                // No saved filter and no session date — seed with default 1-year range
                dispatch(setQuery({
                    query: { [AnalyticsFilter.Date]: defaultDateRange },
                    type: QueryType.Analytics,
                }))
            }
        }
    }, [selectedDashboard])

    // Reset unsaved changes when a tile is no longer active
    useEffect(() => {
        if (!activeTile) {
            setHasUnsavedChanges(false)
            setCurrentConfig(null)
        }
    }, [activeTile])

    // Initialize edit header fields when entering edit mode
    useEffect(() => {
        if (editMode && activeTile) {
            const config = activeTile.config || {}
            setEditName(config.name || '')
            setEditIcon(config.icon || 'fas fa-chart-bar')
            setEditDescription(config.description || '')
        }
    }, [editMode])

    // Update total pages when tiles or layout mode changes
    useEffect(() => {
        const tilesPerPage = getTilesPerPage()
        // Account for the "Add Analytics Tile" button as an additional tile
        const totalItems = tiles.length

        // Calculate how many pages are needed for just the tiles
        const pagesForTiles = Math.ceil(totalItems / tilesPerPage)

        // Check if the last page is completely filled with tiles
        const itemsOnLastPage = totalItems % tilesPerPage
        const isLastPageFull = itemsOnLastPage === 0 && totalItems > 0

        // If the last page is full, we need an additional page for the "Add Analytics Tile" button
        const totalPagesCount = isLastPageFull ? pagesForTiles + 1 : pagesForTiles || 1

        setTotalPages(totalPagesCount)

        // Reset to first page if currentPage is now invalid
        if (currentPage > totalPagesCount) {
            setCurrentPage(1)
        }
    }, [tiles, layoutMode])

    const createDefaultDashboard = async () => {
        try {
            const result = await dispatch(createAnalyticsDashboard({
                name: 'Default Dashboard',
                appId: app?.meta?.id || null,
                organizationId: organization?.id || null,
                tiles: []
            }))
            return result.payload?.dashboard != null
        } catch (error) {
            logger.error('Error creating default dashboard', error)
            return false
        }
    }

    const loadDashboards = async (force = false) => {
        if (initialLoad.current && !force) {
            return
        }
        initialLoad.current = 1
        setLoading(true)
        try {
            const dashboardsRequest = buildAnalyticsDashboardsRequest({
                appId: app?.meta?.id,
                organizationId: organization?.id,
            })
            const dashboardsResult = await queryClient.fetchQuery({
                queryKey: dashboardsRequest.queryKey,
                queryFn: () => fetchAnalyticsDashboardsRequest(dashboardsRequest),
                staleTime: force ? 0 : 5 * 60 * 1000,
            })

            if (dashboardsResult?.dashboards && dashboardsResult.dashboards.length > 0) {
                const normalizedDashboards = dashboardsResult.dashboards.map(normalizeDashboard)

                // Save the dashboards in state
                setDashboards(normalizedDashboards)

                // Priority order for dashboard selection:
                // 1. Default dashboard (if one exists)
                // 2. Last selected dashboard from localStorage
                // 3. First dashboard in the list

                const defaultDashboard = normalizedDashboards.find(d => d.isDefault === true || d.isDefault === 1)
                const lastSelectedId = localStorage.getItem('selectedDashboardId')
                const lastSelected = normalizedDashboards.find(d => d.id?.toString() === lastSelectedId)

                if (defaultDashboard) {
                    setSelectedDashboard(defaultDashboard)
                } else if (lastSelected) {
                    setSelectedDashboard(lastSelected)
                } else {
                    // Fallback to the first dashboard if no default or previously selected dashboard
                    setSelectedDashboard(normalizedDashboards[0])
                }
            } else {
                // No dashboards exist, create a default one, but only if we're not already in the process
                // of creating one (prevents duplicate creation in React strict mode)
                setDashboards([])
                setTiles([])

                if (!defaultDashboardCreationInProgress.current) {
                    defaultDashboardCreationInProgress.current = true
                    try {
                        const created = await createDefaultDashboard()
                        if (created) {
                            await queryClient.invalidateQueries({ queryKey: ['analytics-dashboards'] })
                            const refreshRequest = buildAnalyticsDashboardsRequest({
                                appId: app?.meta?.id,
                                organizationId: organization?.id,
                            })
                            const refreshResult = await queryClient.fetchQuery({
                                queryKey: refreshRequest.queryKey,
                                queryFn: () => fetchAnalyticsDashboardsRequest(refreshRequest),
                                staleTime: 0,
                            })

                            if (refreshResult?.dashboards && refreshResult.dashboards.length > 0) {
                                const normalizedDashboards = refreshResult.dashboards.map(normalizeDashboard)
                                setDashboards(normalizedDashboards)
                                setSelectedDashboard(normalizedDashboards[0])
                            }
                        }
                    } finally {
                        // Reset the flag after some time to allow for future dashboard creation
                        // if needed (but not during the initial double render in strict mode)
                        setTimeout(() => {
                            defaultDashboardCreationInProgress.current = false
                        }, 2000)
                    }
                }
            }
        } catch (error) {
            logger.error('Error loading analytics dashboards', error)
            setDashboards([])
            setTiles([])
        } finally {
            setLoading(false)
        }
        initialLoad.current = 2
    }

    const loadDashboardTiles = async (dashboardId: string, clear = true, force = false) => {
        if (loading) {
            return
        }

        if (clear) {
            setLoading(true)
        }
        try {
            const tilesRequest = buildAnalyticsTilesRequest({ dashboardId })
            const tilesResult = await queryClient.fetchQuery({
                queryKey: tilesRequest.queryKey,
                queryFn: () => fetchAnalyticsTilesRequest(tilesRequest),
                staleTime: force ? 0 : 5 * 60 * 1000,
            })

            if (tilesResult?.tiles) {
                setDelayTileRender(true)
                setTiles(tilesResult.tiles)
                setTimeout(() => {
                    setDelayTileRender(false)
                }, 10)

            } else {
                setTiles([])
            }

            // Reset any unsaved changes state when loading a new dashboard
            setHasUnsavedChanges(false)
            setCurrentConfig(null)

            // Save the selected dashboard ID for persistence
            localStorage.setItem('selectedDashboardId', dashboardId)
        } catch (error) {
            logger.error('Error loading dashboard tiles', error)
            setTiles([])
        } finally {
            setLoading(false)
        }
    }

    const handleDashboardChange = (dashboard: any) => {
        setSelectedDashboard(normalizeDashboard(dashboard))
    }

    const handleAddDashboard = () => {
        dispatch(showPrompt({
            type: PromptType.Input,
            title: 'Create New Dashboard',
            message: 'Enter a name for the new dashboard:',
            placeholder: 'Dashboard name',
            confirmText: 'Create',
            cancelText: 'Cancel'
        })).then((result) => {
            if (result && result.payload) {
                const dashboardName = result.payload.trim()
                if (dashboardName) {
                    handleCreateDashboard(dashboardName)
                }
            }
        })
    }

    const handleCreateDashboard = async (dashboardName: string) => {
        if (!dashboardName.trim()) {
            dispatch(showPrompt({
                type: PromptType.Error,
                title: 'Invalid Name',
                message: 'Dashboard name cannot be empty'
            }))
            return
        }

        try {
            const result = await dispatch(createAnalyticsDashboard({
                name: dashboardName,
                appId: app?.meta?.id || null,
                organizationId: organization?.id || null,
                tiles: []
            }))

            if (result.payload?.dashboard) {
                await queryClient.invalidateQueries({ queryKey: ['analytics-dashboards'] })
                loadDashboards(true)

                // Select the newly created dashboard
                setSelectedDashboard(result.payload.dashboard)
            }
        } catch (error) {
            dispatch(showPrompt({
                type: PromptType.Message,
                title: 'Error',
                message: 'There was an error creating the dashboard'
            }))
        }
    }

    const handleRenameDashboard = () => {
        if (selectedDashboard) {
            dispatch(showPrompt({
                type: PromptType.Input,
                title: 'Rename Dashboard',
                message: 'Enter a new name for the dashboard:',
                placeholder: 'Dashboard name',
                inputOptions: {
                    defaultValue: selectedDashboard.name,
                },
                confirmText: 'Update',
                cancelText: 'Cancel'
            })).then((result) => {
                if (result && result.payload) {
                    const dashboardName = result.payload.trim()
                    if (dashboardName) {
                        handleUpdateDashboardName(dashboardName)
                    }
                }
            })
        }
    }

    const handleUpdateDashboardName = async (newName: string) => {
        if (!newName.trim() || !selectedDashboard) {
            dispatch(showPrompt({
                type: PromptType.Confirm,
                title: 'Invalid Name',
                message: 'Dashboard name cannot be empty'
            }))
            return
        }

        try {
            const result = await dispatch(updateAnalyticsDashboard({
                dashboardId: selectedDashboard.id,
                data: { name: newName }
            }))

            if (result.payload?.dashboard) {
                dispatch(showPrompt({
                    type: PromptType.Success,
                    title: 'Dashboard Updated',
                    message: 'Dashboard name has been updated'
                }))

                await queryClient.invalidateQueries({ queryKey: ['analytics-dashboards'] })
                loadDashboards(true)
            }
        } catch (error) {
            logger.error(error)
            dispatch(showPrompt({
                type: PromptType.Confirm,
                title: 'Error',
                message: 'There was an error updating the dashboard'
            }))
        }
    }

    const handleDeleteDashboard = async () => {
        if (!selectedDashboard) return

        if (window.confirm(`Are you sure you want to delete "${selectedDashboard.name}"?`)) {
            try {
                await dispatch(removeAnalyticsDashboard(selectedDashboard.id))

                dispatch(showPrompt({
                    type: PromptType.Success,
                    title: 'Dashboard Deleted',
                    message: 'Analytics dashboard has been deleted successfully'
                }))

                // Refresh dashboards list
                await queryClient.invalidateQueries({ queryKey: ['analytics-dashboards'] })
                loadDashboards(true)
            } catch (error) {
                dispatch(showPrompt({
                    type: PromptType.Error,
                    title: 'Error',
                    message: 'There was an error deleting the dashboard'
                }))
            }
        }
    }

    const handleSetDashboardDefault = async () => {
        if (!selectedDashboard) return

        try {
            await dispatch(setAnalyticsDashboardDefault({
                dashboardId: selectedDashboard.id,
                appId: app?.meta?.id,
                organizationId: organization?.id
            }))

            // Update the local state immediately to reflect the changes
            const updatedDashboards = dashboards.map(dashboard => ({
                ...dashboard,
                isDefault: dashboard.id === selectedDashboard.id ? true : false
            }))

            // Update the selected dashboard to reflect its new default status
            const updatedSelectedDashboard = {
                ...selectedDashboard,
                isDefault: true
            }

            // Update both state variables
            setDashboards(updatedDashboards)
            setSelectedDashboard(updatedSelectedDashboard)

            dispatch(showPrompt({
                type: PromptType.Success,
                title: 'Default Dashboard Set',
                message: `"${selectedDashboard.name}" has been set as the default dashboard`
            }))

            // Optionally refresh dashboards from server to ensure consistency
            // This provides a backup in case the local state update doesn't cover all edge cases
            setTimeout(() => {
                loadDashboards(true)
            }, 100)
        } catch (error) {
            logger.error('Error setting dashboard as default', error)
            dispatch(showPrompt({
                type: PromptType.Error,
                title: 'Error',
                message: 'There was an error setting the dashboard as default'
            }))
        }
    }

    const handleAddTile = () => {
        handleCreateTileWithConfig()
    }

    const handleViewTile = (tile: AnalyticsTileItem) => {
        setTileViewKey(k => k + 1)
        setActiveTile(tile)
        setEditMode(false)
        setHasUnsavedChanges(false)
        setCurrentConfig(null)
    }

    const handleBackToDashboard = (force = false) => {
        if (hasUnsavedChanges && !force) {
            dispatch(showPrompt({
                type: PromptType.Confirm,
                title: 'Unsaved Changes',
                message: 'You have unsaved changes. Do you want to discard them?',
                confirmText: 'Discard',
                cancelText: 'Cancel'
            })).then((result) => {
                if (result && result.payload) {
                    if (result.payload) {
                        setTileViewKey(k => k + 1)
                        setActiveTile(null)
                        setEditMode(false)
                        setHasUnsavedChanges(false)
                    }
                }
            })
        } else {
            setActiveTile(null)
            setEditMode(false)
            setHasUnsavedChanges(false)
        }
    }

    const handleCancelTileEdit = () => {
        if (hasUnsavedChanges) {
            dispatch(showPrompt({
                type: PromptType.Confirm,
                title: 'Unsaved Changes',
                message: 'You have unsaved changes. Do you want to discard them?',
                confirmText: 'Discard',
                cancelText: 'Cancel'
            })).then((result) => {
                if (result && result.payload) {
                    setEditMode(false)
                    setHasUnsavedChanges(false)
                    setCurrentConfig(null)
                }
            })
            return
        }

        setEditMode(false)
        setHasUnsavedChanges(false)
        setCurrentConfig(null)
    }

    const handleTileConfigChange = (config: Dict) => {
        setHasUnsavedChanges(true)
        setCurrentConfig(config)
        // Keep header edit fields in sync when wizard/aggregation changes the name or icon
        if (config.name !== undefined) setEditName(config.name)
        if (config.icon !== undefined) setEditIcon(config.icon)
        if (config.description !== undefined) setEditDescription(config.description || '')
    }

    const handleEditFieldChange = (field: 'name' | 'icon' | 'description', value: string) => {
        if (field === 'name') setEditName(value)
        else if (field === 'icon') setEditIcon(value)
        else if (field === 'description') setEditDescription(value)
        setHasUnsavedChanges(true)
        setCurrentConfig((prev) => ({
            ...(prev || activeTile?.config || {}),
            [field]: value,
        }))
    }

    function handleTileConfigCreate(config: Dict) {
        setCurrentConfig(config)
        handleSaveAnalyticsConfig(config)
    }

    const handleSaveAnalyticsConfig = (config: Dict) => {
        // If we have an active tile, update it and return to dashboard
        if (activeTile) {
            dispatch(updateAnalyticsTile({
                tileId: activeTile.id,
                data: {
                    appId: app?.meta?.id || null,
                    organizationId: organization?.id || null,
                    analyticsDashboardId: selectedDashboard.id,
                    config,
                    name: config.name,
                }
            }))
                .then(() => {
                    // Update config of tile
                    const idx = tiles.findIndex((x) => x.id === activeTile.id)
                    if (idx >= 0) {
                        const updatedTiles = [...tiles]
                        updatedTiles[idx] = { ...updatedTiles[idx], config, name: config.name }
                        setTiles(updatedTiles)
                    }
                    setEditMode(false)
                    setHasUnsavedChanges(false)
                    setCurrentConfig(null)
                })
                .catch(() => {
                    dispatch(showPrompt({
                        type: PromptType.Confirm,
                        title: 'Error',
                        message: 'There was an error updating the tile'
                    }))
                })
        } else {
            handleCreateTileWithConfig(config)
        }
    }

    const handleCreateTileWithConfig = async (config: Dict = {}) => {
        if (!selectedDashboard) {
            dispatch(showPrompt({
                type: PromptType.Error,
                title: 'No Dashboard Selected',
                message: 'Please select a dashboard to add this tile to'
            }))
            return
        }

        try {
            // Calculate the position for the new tile to be at the end of the list
            let highestPosition = 0
            if (tiles.length > 0) {
                // Find the highest current position value
                highestPosition = tiles.reduce((max, tile) => {
                    const tilePosition = tile.config?.position !== undefined ? tile.config.position : 0
                    return Math.max(max, tilePosition)
                }, 0)
            }

            // Create a new config with position set to be after all existing tiles
            const newConfig = {
                ...config,
                position: highestPosition + 1 // Set position to be one more than the highest existing position
            }

            const ret = await dispatch(createAnalyticsTile({
                name: config?.name || 'New Analytics Tile',
                appId: app?.meta?.id || null,
                organizationId: organization?.id || null,
                analyticsDashboardId: selectedDashboard.id,
                config: newConfig,
                position: highestPosition + 1 // Also set the position directly on the tile object
            }))
            await queryClient.invalidateQueries({ queryKey: ['analytics-dashboard-tiles', selectedDashboard.id] })
            loadDashboardTiles(selectedDashboard.id, false, true)
        } catch (error) {
            dispatch(showPrompt({
                type: PromptType.Confirm,
                title: 'Error',
                message: 'There was an error creating the tile'
            }))
        }
    }

    const handleDashboardAction = (action: string) => {
        switch (action) {
            case 'new':
                handleAddDashboard()
                break
            case 'rename':
                handleRenameDashboard()
                break
            case 'delete':
                handleDeleteDashboard()
                break
            case 'set-default':
                handleSetDashboardDefault()
                break
        }
    }

    const handleRemoveAnalyticsTile = () => {
        dispatch(showPrompt({
            type: PromptType.Confirm,
            title: 'Remove Analytics Tile',
            message: `Are you sure you want to remove "${activeTile.name}"?`,
            confirmText: 'Remove',
            cancelText: 'Cancel'
        })).then((result) => {
            if (result && result.payload) {
                // User confirmed deletion
                dispatch(removeAnalyticsTile({ tileId: activeTile.id }))
                    .then(() => {
                        handleBackToDashboard(true)
                        // Refresh the tiles after removal
                        if (selectedDashboard) {
                            queryClient.invalidateQueries({ queryKey: ['analytics-dashboard-tiles', selectedDashboard.id] })
                                .then(() => loadDashboardTiles(selectedDashboard.id, false, true))
                        }
                    })
                    .catch((error) => {
                        logger.error('Error removing analytics tile', error)
                        dispatch(showPrompt({
                            type: PromptType.Confirm,
                            title: 'Error',
                            message: 'There was an error removing the analytics tile'
                        }))
                    })
            }
        })
    }

    // Handle tile operations in dashboard edit mode
    const handleTileOperation = async (tileId: string, operation: string, _tile: Dict) => {
        if (!selectedDashboard) return

        try {
            const tile = _tile || tiles.find(t => t.id === tileId)
            if (!tile) return

            // Clone the current tiles array to work with
            const updatedTiles = [...tiles]
            const tileIndex = updatedTiles.findIndex(t => t.id === tileId)
            if (tileIndex === -1) return

            let updatedTile = { ...updatedTiles[tileIndex] }
            let updateData = {}

            switch (operation) {
                case 'delete':
                    dispatch(showPrompt({
                        type: PromptType.Confirm,
                        title: 'Delete Tile',
                        message: `Are you sure you want to delete "${tile.name}"?`,
                        confirmText: 'Delete',
                        cancelText: 'Cancel'
                    })).then((result) => {
                        if (result && result.payload) {
                            dispatch(removeAnalyticsTile({ tileId }))
                                .then(() => {
                                    queryClient.invalidateQueries({ queryKey: ['analytics-dashboard-tiles', selectedDashboard.id] })
                                    // Remove tile from local state
                                    const filteredTiles = tiles.filter(t => t.id !== tileId)

                                    // Calculate pagination after removal
                                    const tilesPerPage = getTilesPerPage()
                                    const newTotalPages = Math.max(
                                        Math.ceil(filteredTiles.length / tilesPerPage) || 1,
                                        1
                                    )

                                    // If we're on a page that no longer exists after deletion,
                                    // navigate to the last available page
                                    if (currentPage > newTotalPages) {
                                        setCurrentPage(newTotalPages)
                                    }

                                    // Update the tiles state to trigger layout recalculation
                                    setTiles(filteredTiles)

                                    // Force a re-render of the tile grid
                                    // First, set the refresh state to trigger a re-render
                                    setRefresh(Date.now())

                                    // Force a DOM reflow to ensure the grid updates immediately
                                    requestAnimationFrame(() => {
                                        // Force DOM reflow by accessing properties that trigger layout recalculation
                                        const grid = document.querySelector('.analytics-dashboard-grid')
                                        if (grid) {
                                            // Reading these properties forces a reflow
                                            const forceReflow = grid.getBoundingClientRect()

                                            // Force all proxy elements to recalculate
                                            document.querySelectorAll('.analytics-dashboard-tile-proxy').forEach(el => {
                                                el.getBoundingClientRect()
                                            })

                                            // Update refresh state again to ensure new positions are calculated
                                            setRefresh(Date.now())
                                        }
                                    })
                                })
                                .catch((error) => {
                                    logger.error('Error deleting tile', error)
                                    dispatch(showPrompt({
                                        type: PromptType.Error,
                                        title: 'Error',
                                        message: 'Failed to delete the tile'
                                    }))
                                })
                        }
                    })
                    break
                case 'view':
                    handleViewTile(updatedTile)
                    break
            }
        } catch (error) {
            logger.error('Error performing tile operation', error)
            dispatch(showPrompt({
                type: PromptType.Error,
                title: 'Error',
                message: `Failed to ${operation} the tile`
            }))
        }
    }

    // Dashboard date filter change handler
    const handleDashboardDateFilterChange = async (dateRange: { startDate: string, endDate: string } | null) => {
        // Update Redux — this is the single source of truth for the analytics date filter
        dispatch(setQuery({
            query: { [AnalyticsFilter.Date]: dateRange },
            type: QueryType.Analytics,
        }))

        // Save date filter to dashboard configuration
        if (selectedDashboard) {
            try {
                const normalizedDashboard = normalizeDashboard(selectedDashboard)
                const updatedLayoutConfig = {
                    ...normalizedDashboard.layoutConfig,
                    dateFilter: dateRange
                }

                await dispatch(updateAnalyticsDashboard({
                    dashboardId: normalizedDashboard.id,
                    data: {
                        name: normalizedDashboard.name,
                        description: normalizedDashboard.description,
                        appId: normalizedDashboard.appId,
                        organizationId: normalizedDashboard.organizationId,
                        layoutConfig: updatedLayoutConfig
                    }
                }))

                // Update local selectedDashboard state
                setSelectedDashboard({
                    ...normalizedDashboard,
                    layoutConfig: updatedLayoutConfig
                })
                await queryClient.invalidateQueries({ queryKey: ['analytics-dashboards'] })
            } catch (error) {
                logger.error('Error saving dashboard date filter', error)
            }
        }
    }

    // Navigation functions for pagination
    const goToNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1)
        }
    }

    const goToPrevPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1)
        }
    }

    const getCurrentPageTiles = () => {
        const startIndex = (currentPage - 1) * TILES_PER_PAGE
        const endIndex = startIndex + TILES_PER_PAGE
        return tiles.slice(startIndex, endIndex)
    }

    const shouldShowAddTileButton = () => {
        const tilesOnCurrentPage = getCurrentPageTiles().length
        const maxTilesPerPage = TILES_PER_PAGE

        // Show the add button if there's space on the current page
        return tilesOnCurrentPage < maxTilesPerPage
    }

    // Helper function to calculate tile positioning based on proxy element
    const calculateTilePosition = (tileId: string, isActive: boolean = false): React.CSSProperties => {
        // Don't calculate position if the tile is active (in full-screen mode)
        if (isActive) return {}

        // Find the proxy element
        const proxy = document.querySelector(`.analytics-dashboard-tile-proxy[data-id="${tileId}"]`)

        if (!proxy) {
            // If proxy isn't found yet, make the tile invisible and trigger a refresh
            setTimeout(() => {
                setRefresh(Date.now())
            }, 0)
            return { transform: 'scale(0)', opacity: 0 }
        }

        // Calculate position relative to parent
        let rect = proxy.getBoundingClientRect()
        const parentRect = proxy.parentElement.getBoundingClientRect()
        rect = {
            left: rect.left - parentRect.left,
            top: rect.top - parentRect.top,
            width: rect.width,
            height: rect.height
        }

        return {
            left: rect.left + 'px',
            width: rect.width + 'px',
            height: rect.height + 'px',
            top: rect.top + 'px',
            opacity: 1,
            transition: 'all 0.3s ease-in-out'
        }
    }

    // Helper function to get tile size class based on layout mode and tile size
    const getTileSizeClass = () => {
        switch (layoutMode) {
            default:
            case DashboardLayout.Single:
                return 1
            case DashboardLayout.Dual:
                return 2
            case DashboardLayout.Grid:
                return 4
        }
    }

    if (loading && initialLoad.current < 2) {
        return <Spinner showAfter={0} />
    }

    const isViewing = activeTile != null
    const isEditing = isViewing && editMode

    // Calculate the tiles to display for the current page
    const tilesPerPage = getTilesPerPage()
    const startIndex = (currentPage - 1) * tilesPerPage
    const endIndex = Math.min(startIndex + tilesPerPage, tiles.length)
    const currentPageTiles = tiles.slice(startIndex, endIndex)

    // Determine if this is the last page
    const isLastPage = currentPage === totalPages

    // Check if we should show the "Add Analytics Tile" button
    // Only show it on the last page and when not in viewing mode
    const shouldShowAddTile = isLastPage && !isViewing

    // Generate array of page numbers for the dropdown
    const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1)

    return (
        <div className={`analytics-dashboard${isViewing ? ' viewing' : ''}`}>
            <div className="analytics-dashboard-header">
                {!isViewing && <div className="analytics-dashboard-title middle">
                    {dashboards.length > 0 && (
                        <div className="dashboard-selector">
                            <DropdownMenu
                                value={selectedDashboard ? selectedDashboard.id : null}
                                text={selectedDashboard ? selectedDashboard.name : 'Select Dashboard'}
                                onChange={(x) => handleDashboardChange(dashboards.find((y) => y.id == x))}
                                items={dashboards.map(dashboard => ({
                                    text: dashboard.isDefault ? `${dashboard.name} (Default)` : dashboard.name,
                                    value: dashboard.id,
                                }))} />
                        </div>
                    )}
                    {dashboards.length == 0 && <Button onClick={handleAddDashboard}>Create Dashboard</Button>}
                </div>}
                {isViewing && <div className="dashboard-controls">
                    <Button icon="fas fa-chevron-left" onClick={handleBackToDashboard}>Back</Button>
                </div>}
                <div className="topbar-portal-slot" ref={topbarSlotRef} style={{ display: isViewing ? 'flex' : 'none', flex: 1, minWidth: 0, alignItems: 'center' }}></div>
                {isViewing && <div className="dashboard-controls">
                    {!isEditing && <Button icon="fas fa-pen" onClick={() => setEditMode(true)}>Edit View</Button>}
                    {isEditing && (
                        <React.Fragment>
                            <Button
                                icon="fas fa-save"
                                disabled={!hasUnsavedChanges}
                                onClick={() => hasUnsavedChanges ? handleSaveAnalyticsConfig(currentConfig) : null}
                                className="save"
                            >
                                Save
                            </Button>
                            <Button
                                icon="fas fa-times"
                                onClick={handleCancelTileEdit}
                                className="cancel"
                            >
                                Cancel
                            </Button>
                        </React.Fragment>
                    )}
                </div>}
                {!isViewing && <div className="dashboard-controls">

                    <DropdownMenu
                        icon="fas fa-cog"
                        text="Dashboard Options"
                        onChange={handleDashboardAction}
                        items={[
                            { text: 'Create New Dashboard', value: 'new', icon: 'fas fa-plus' },
                            selectedDashboard ? { text: 'Rename Dashboard', value: 'rename', icon: 'fas fa-pen' } : null,
                            (selectedDashboard && dashboards.length > 1) ? { text: 'Delete Dashboard', value: 'delete', icon: 'fas fa-trash' } : null,
                            (selectedDashboard && !selectedDashboard.isDefault) ? {
                                text: 'Set as Default',
                                value: 'set-default',
                                icon: 'fas fa-star'
                            } : null
                        ].filter(Boolean)}
                    />

                    {/* Dashboard Date Filter */}
                    <div className="dashboard-date-filter">
                        <DateRange
                            selected={analyticsDateQuery}
                            onChange={handleDashboardDateFilterChange}
                            className={analyticsDateQuery ? 'filtered' : ''}
                            initialize={false}
                        />
                    </div>

                    {/* Layout toggle dropdown */}
                    <DropdownMenu
                        icon={layoutMode === DashboardLayout.Single ? "fas fa-square" :
                            layoutMode === DashboardLayout.Dual ? "fas fa-columns" :
                                "fas fa-th-large"}
                        text="Layout"
                        value={layoutMode}
                        onChange={(value) => setLayoutMode(value as DashboardLayout)}
                        items={[
                            { text: 'Single View', value: DashboardLayout.Single, icon: 'fas fa-square' },
                            { text: 'Split View', value: DashboardLayout.Dual, icon: 'fas fa-columns' },
                            { text: 'Grid View', value: DashboardLayout.Grid, icon: 'fas fa-th-large' }
                        ]}
                    />

                    {/* Left pagination control */}
                    {!isViewing && totalPages > 1 && <div className="pagination">
                        <div className="pagination-control left">
                            <IconButton
                                icon="fas fa-chevron-left"
                                onClick={goToPrevPage}
                                disabled={currentPage <= 1}
                                className={currentPage <= 1 ? 'disabled' : ''}
                            />
                        </div>
                        <div className="pagination-indicator">
                            <span>Page {currentPage} of {totalPages}</span>
                        </div>
                        <div className="pagination-control right">
                            <IconButton
                                icon="fas fa-chevron-right"
                                onClick={goToNextPage}
                                disabled={currentPage >= totalPages}
                                className={currentPage >= totalPages ? 'disabled' : ''}
                            />
                        </div>
                    </div>}
                </div>}
            </div>

            {
                loading ? (
                    <div className="center-content">
                        <Spinner showAfter={0} />
                    </div>
                ) : tiles.length > 0 || !isViewing ? (
                    <div className="analytics-dashboard-container" data-layout={layoutMode}>

                        <div className="analytics-dashboard-grid" data-layout={layoutMode}>
                            {currentPageTiles.map((tile, ix) => {
                                const size = getTileSizeClass()
                                return <div className="analytics-dashboard-tile-proxy" data-id={tile.id} data-size={size} key={`proxy-${tile.id}`} />
                            })}

                            {/* Add tile proxy placeholder for the "Add Tile" tile if not in viewing mode and on the last page */}
                            {!isViewing && currentPage === totalPages && (
                                <div className="analytics-dashboard-tile-proxy" data-id="add-tile" data-size={1} key="proxy-add-tile" />
                            )}

                            {!delayTileRender && currentPageTiles.map((tile, ix) => {
                                const isActive = activeTile && activeTile.id === tile.id
                                const tileSize = getTileSizeClass(tileSize)

                                // Build class name based on size and edit mode
                                let className = "analytics-dashboard-tile"
                                className += ` size-${tileSize}`

                                let dataMode = ""
                                let focus = false

                                if (isActive) {
                                    focus = true
                                    if (isEditing) {
                                        dataMode = "edit"
                                    } else {
                                        dataMode = "view"
                                    }
                                } else if (isViewing) {
                                    dataMode = "hide"
                                }

                                // Use the helper function to calculate position
                                const style = calculateTilePosition(tile.id, focus)

                                return (
                                    <div
                                        className={className}
                                        data-mode={dataMode}
                                        style={style}
                                        key={tile.id}
                                        data-position={tile.config?.position || ix}
                                        data-size={tileSize}
                                    >
                                        {/* Tile edit controls shown when in dashboard edit mode */}
                                        {!isViewing && (
                                            <div className="tile-edit-controls">
                                                <IconButton
                                                    noBg
                                                    icon={"fas fa-expand"}
                                                    onClick={() => handleTileOperation(tile.id, 'view')}
                                                    title={`Expand`}
                                                />
                                                <DropdownMenu icon="fas fa-cog"
                                                    buttonClass="no-bg"
                                                    items={[
                                                        { text: 'Delete Tile', value: 'delete', icon: 'fas fa-trash' }, ,
                                                    ]}
                                                    onChange={(value) => handleTileOperation(tile.id, value)} />

                                            </div>
                                        )}

                                        <Card key={tile.id}>
                                            <AnalyticsTable
                                                key={isActive ? `${tile.id}-${tileViewKey}` : tile.id}
                                                id={tile.id}
                                                tile={tile}
                                                app={app}
                                                definitions={definitions}
                                                filters={filters}
                                                optionalFilters={optionalFilters}
                                                searchFilters={searchFilters}
                                                additionalMaps={additionalMaps}
                                                readOnly={dataMode != 'edit'}
                                                skipWizard={false}
                                                defaultViewMode={tile.config?.viewMode || 'chart'}
                                                initialConfig={tile.config}
                                                isDashboardTile={true}  // Mark as dashboard tile
                                                onChange={isEditing ? handleTileConfigChange : null}
                                                onCreate={handleTileConfigCreate}
                                                onTileOperation={handleTileOperation}
                                                onWizardCancel={handleBackToDashboard}
                                                compactMode={!isViewing}
                                                marginModifier={marginModifier}
                                                topbarPortalTarget={isActive ? topbarPortalEl : null}
                                                />
                                        </Card>
                                    </div>
                                )
                            })}

                            {/* Add Tile button, displayed as a tile at the end of the list, only on the last page */}
                            {!isViewing && currentPage === totalPages && (
                                <div
                                    className="analytics-dashboard-tile add-tile"
                                    onClick={handleAddTile}
                                    style={calculateTilePosition('add-tile')}
                                >
                                    <div className="add-tile-content">
                                        <i className="fas fa-plus"></i>
                                        <div>Add Analytics Tile</div>
                                    </div>
                                </div>
                            )}
                        </div>

                    </div>
                ) : (
                    <div className="center-content empty-state">
                        <p>No analytics tiles found. Create your first tile to get started.</p>
                        <Button
                            icon="fas fa-plus"
                            onClick={handleCreateTile}
                        >
                            Add Analytics Tile
                        </Button>
                    </div>
                )
            }
        </div>
    )
}